// =====================================================
// TEMPLATE ACCESS MANAGEMENT MODAL
// Manage access grants for private templates
// =====================================================

import React, { useState, useEffect } from 'react';
import { Template, templateService } from '../../services/templateService';
import { useLanguage } from '../../state/language/LanguageProvider';

interface TemplateAccessManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: Template;
  onAccessChange?: () => void;
}

interface AccessGrant {
  id: string;
  tenant_id: string; // REQUIRED: User's tenant
  user_id: string; // REQUIRED: Specific user
  access_type: 'READ' | 'WRITE' | 'ADMIN';
  granted_by: string;
  granted_at: string;
  expires_at?: string;
  tenant_name?: string;
  user_email?: string;
  user_first_name?: string;
  user_last_name?: string;
}

interface AccessRequest {
  id: string;
  template_id: string;
  requested_by_user_id: string;
  requested_by_tenant_id: string;
  request_message?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  created_at: string;
  requester_email?: string;
  first_name?: string;
  last_name?: string;
  template_name?: string;
}

export const TemplateAccessManagementModal: React.FC<TemplateAccessManagementModalProps> = ({
  isOpen,
  onClose,
  template,
  onAccessChange
}) => {
  const { t } = useLanguage();
  const [accessGrants, setAccessGrants] = useState<AccessGrant[]>([]);
  const [pendingRequests, setPendingRequests] = useState<AccessRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'requests' | 'grants'>('requests');
  const [tenantId, setTenantId] = useState('');
  const [tenantSearch, setTenantSearch] = useState('');
  const [tenantResults, setTenantResults] = useState<Array<{ id: string; name: string; domain?: string }>>([]);
  const [showTenantDropdown, setShowTenantDropdown] = useState(false);
  const [userId, setUserId] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState<Array<{ id: string; email: string; first_name?: string; last_name?: string; tenant_id: string }>>([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [expiresAt, setExpiresAt] = useState('');
  const [granting, setGranting] = useState(false);

  // Helper to get default expiration (today at 11:59 PM)
  const getDefaultExpiration = (): string => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T23:59`;
  };

  useEffect(() => {
    if (isOpen && template.marketplace_visibility === 'PRIVATE') {
      loadData();
    }
  }, [isOpen, template.id]);

  // Debounced tenant search
  useEffect(() => {
    if (!tenantSearch || tenantSearch.length < 2) {
      setTenantResults([]);
      setShowTenantDropdown(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        const results = await templateService.searchTenants(tenantSearch);
        setTenantResults(results);
        setShowTenantDropdown(true);
      } catch (error) {
        console.error('Error searching tenants:', error);
        setTenantResults([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [tenantSearch]);

  // Debounced user search
  useEffect(() => {
    if (!userSearch || userSearch.length < 2) {
      setUserResults([]);
      setShowUserDropdown(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        const results = await templateService.searchUsers(userSearch, tenantId || undefined);
        setUserResults(results);
        setShowUserDropdown(true);
      } catch (error) {
        console.error('Error searching users:', error);
        setUserResults([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [userSearch, tenantId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [grants, requests] = await Promise.all([
        templateService.listAccessGrants(template.id),
        templateService.getPendingAccessRequests()
      ]);
      setAccessGrants(grants);
      // Filter requests for this template
      const templateRequests = requests.filter((r: any) => r.template_id === template.id);
      setPendingRequests(templateRequests);
    } catch (error) {
      console.error('Error loading access data:', error);
      alert(t('templates.access.failedToLoad') || 'Failed to load access data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGrantAccess = async () => {
    if (!tenantId) {
      alert(t('templates.access.pleaseEnterTenantId') || 'Please enter a tenant ID');
      return;
    }
    if (!userId) {
      alert(t('templates.access.pleaseEnterUserId') || 'Please enter a user ID - access is granted to a specific user within a tenant');
      return;
    }

    setGranting(true);
    try {
      await templateService.grantAccess(
        template.id,
        tenantId, // REQUIRED: User's tenant
        userId, // REQUIRED: Specific user
        'READ', // Default to READ access
        expiresAt ? new Date(expiresAt) : undefined
      );
      
      // Reset form
      setTenantId('');
      setTenantSearch('');
      setUserId('');
      setUserSearch('');
      setExpiresAt(getDefaultExpiration());
      
      // Reload data
      await loadData();
      
      if (onAccessChange) {
        onAccessChange();
      }
      
      alert(t('templates.access.grantedSuccess') || 'Access granted successfully!');
    } catch (error: any) {
      console.error('Error granting access:', error);
      alert(error.response?.data?.error || t('templates.access.failedToGrant') || 'Failed to grant access. Please try again.');
    } finally {
      setGranting(false);
    }
  };

  const handleRevokeAccess = async (grantId: string) => {
    if (!confirm(t('templates.access.confirmRevoke') || 'Are you sure you want to revoke this access?')) {
      return;
    }

    try {
      await templateService.revokeAccess(template.id, grantId);
      await loadData();
      
      if (onAccessChange) {
        onAccessChange();
      }
      
      alert(t('templates.access.revokedSuccess') || 'Access revoked successfully!');
    } catch (error: any) {
      console.error('Error revoking access:', error);
      alert(error.response?.data?.error || t('templates.access.failedToRevoke') || 'Failed to revoke access. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{t('templates.access.manageAccess') || 'Manage Access'}</h2>
              <p className="text-sm text-gray-500 mt-1">{template.name}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Tabs */}
          <div className="mb-6 border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('requests')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'requests'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {t('templates.access.pendingRequests') || 'Pending Requests'} ({pendingRequests.length})
              </button>
              <button
                onClick={() => setActiveTab('grants')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'grants'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {t('templates.access.accessGrants') || 'Access Grants'} ({accessGrants.length})
              </button>
            </nav>
          </div>

          {/* Pending Requests Tab */}
          {activeTab === 'requests' && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('templates.access.pendingAccessRequests') || 'Pending Access Requests'}</h3>
              
              {loading ? (
                <div className="text-center py-8 text-gray-500">{t('common.loading') || 'Loading...'}</div>
              ) : pendingRequests.length === 0 ? (
                <div className="text-center py-8 text-gray-500">{t('templates.access.noPendingRequests') || 'No pending access requests'}</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('templates.access.requester') || 'Requester'}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('templates.access.requestedAt') || 'Requested At'}
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('templates.access.actions') || 'Actions'}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {pendingRequests.map((request) => (
                        <tr key={request.id}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            <div>
                              <div className="font-medium">
                                {request.requester_email || t('templates.access.unknown') || 'Unknown'}
                              </div>
                              {(request.first_name || request.last_name) && (
                                <div className="text-xs text-gray-500">
                                  {request.first_name} {request.last_name}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {new Date(request.created_at).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium space-x-2">
                            <button
                              onClick={async () => {
                                try {
                                  await templateService.approveAccessRequest(request.id);
                                  await loadData();
                                  if (onAccessChange) {
                                    onAccessChange();
                                  }
                                } catch (error) {
                                  console.error('Error approving request:', error);
                                  alert(error instanceof Error ? error.message : t('templates.access.failedToApprove') || 'Failed to approve request');
                                }
                              }}
                              className="text-green-600 hover:text-green-900 transition-colors"
                            >
                              {t('templates.access.approve') || 'Approve'}
                            </button>
                            <button
                              onClick={async () => {
                                if (confirm(t('templates.access.confirmReject') || 'Are you sure you want to reject this access request?')) {
                                  try {
                                    await templateService.rejectAccessRequest(request.id);
                                    await loadData();
                                    if (onAccessChange) {
                                      onAccessChange();
                                    }
                                  } catch (error) {
                                    console.error('Error rejecting request:', error);
                                    alert(error instanceof Error ? error.message : t('templates.access.failedToReject') || 'Failed to reject request');
                                  }
                                }
                              }}
                              className="text-red-600 hover:text-red-900 transition-colors"
                            >
                              {t('templates.access.reject') || 'Reject'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Access Grants Tab */}
          {activeTab === 'grants' && (
            <>
          {/* Grant New Access */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('templates.access.grantAccessToUser') || 'Grant Access to User'}</h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('templates.access.grantAccessDescription') || 'Access is granted to a specific user within a tenant. Not all users in a tenant automatically get access - each user needs explicit access. When a user saves/copies a template, they can do whatever they want with it.'}
            </p>
            
            <div className="space-y-4">
              {/* Tenant Search (Required) */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('templates.access.tenant') || 'Tenant'} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={tenantSearch}
                  onChange={(e) => {
                    setTenantSearch(e.target.value);
                    if (!e.target.value) {
                      setTenantId('');
                    }
                  }}
                  onFocus={() => {
                    if (tenantResults.length > 0) setShowTenantDropdown(true);
                  }}
                  onBlur={() => {
                    // Delay to allow click on dropdown item
                    setTimeout(() => setShowTenantDropdown(false), 200);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('templates.access.tenantPlaceholder') || 'Start typing tenant name...'}
                  required
                />
                {tenantId && (
                  <input type="hidden" value={tenantId} />
                )}
                {showTenantDropdown && tenantResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    {tenantResults.map((tenant) => (
                      <div
                        key={tenant.id}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                        onClick={() => {
                          setTenantId(tenant.id);
                          setTenantSearch(tenant.name);
                          setShowTenantDropdown(false);
                          // Clear user selection when tenant changes
                          setUserId('');
                          setUserSearch('');
                        }}
                      >
                        <div className="font-medium text-gray-900">{tenant.name}</div>
                        {tenant.domain && (
                          <div className="text-sm text-gray-500">{tenant.domain}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* User Search (Required) */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('templates.access.userEmail') || 'User Email'} <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={userSearch}
                  onChange={(e) => {
                    setUserSearch(e.target.value);
                    if (!e.target.value) {
                      setUserId('');
                    }
                  }}
                  onFocus={() => {
                    if (userResults.length > 0) setShowUserDropdown(true);
                  }}
                  onBlur={() => {
                    // Delay to allow click on dropdown item
                    setTimeout(() => setShowUserDropdown(false), 200);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('templates.access.userEmailPlaceholder') || 'Start typing user email...'}
                  required
                  disabled={!tenantId}
                />
                {userId && (
                  <input type="hidden" value={userId} />
                )}
                {!tenantId && (
                  <p className="text-xs text-gray-500 mt-1">
                    {t('templates.access.selectTenantFirst') || 'Please select a tenant first'}
                  </p>
                )}
                {showUserDropdown && userResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    {userResults.map((user) => (
                      <div
                        key={user.id}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                        onClick={() => {
                          setUserId(user.id);
                          setUserSearch(user.email);
                          setShowUserDropdown(false);
                        }}
                      >
                        <div className="font-medium text-gray-900">{user.email}</div>
                        {(user.first_name || user.last_name) && (
                          <div className="text-sm text-gray-500">
                            {user.first_name} {user.last_name}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Expires At (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('templates.access.expiresAt') || 'Expires At'} ({t('templates.access.optional') || 'Optional'})
                </label>
                <input
                  type="datetime-local"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  onFocus={(e) => {
                    // If empty, set default to today at 11:59 PM
                    if (!e.target.value) {
                      setExpiresAt(getDefaultExpiration());
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('templates.access.expiresAtDescription') || 'Defaults to 11:59 PM of the selected date. Leave empty for no expiration.'}
                </p>
              </div>

              {/* Grant Button */}
              <button
                onClick={handleGrantAccess}
                disabled={granting || !tenantId || !userId}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {granting ? (t('templates.access.granting') || 'Granting...') : (t('templates.access.grantAccessToUser') || 'Grant Access to User')}
              </button>
            </div>
          </div>

          {/* Existing Access Grants */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('templates.access.currentAccessGrants') || 'Current Access Grants'}</h3>
            
            {loading ? (
              <div className="text-center py-8 text-gray-500">{t('common.loading') || 'Loading...'}</div>
            ) : accessGrants.length === 0 ? (
              <div className="text-center py-8 text-gray-500">{t('templates.access.noAccessGrants') || 'No access grants yet'}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('templates.access.tenant') || 'Tenant'}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('templates.access.user') || 'User'}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('templates.access.accessType') || 'Access Type'}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('templates.access.grantedAt') || 'Granted At'}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('templates.access.expiresAt') || 'Expires At'}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('templates.access.actions') || 'Actions'}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {accessGrants.map((grant) => (
                      <tr key={grant.id}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {grant.tenant_name || grant.tenant_id || t('common.notAvailable') || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {grant.user_email 
                            ? `${grant.user_first_name || ''} ${grant.user_last_name || ''} (${grant.user_email})`.trim()
                            : grant.user_id || t('common.notAvailable') || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {grant.access_type}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {new Date(grant.granted_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {grant.expires_at ? new Date(grant.expires_at).toLocaleString() : (t('templates.access.never') || 'Never')}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleRevokeAccess(grant.id)}
                            className="text-red-600 hover:text-red-900 transition-colors"
                          >
                            {t('templates.access.revoke') || 'Revoke'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
          >
            {t('common.close') || 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
};

